import type { QueryClient } from '@tanstack/svelte-query';
import { getQuery } from '#lib/query-utils/getQuery.js';
import {
	PRIMARY_GALLERY_LIMIT,
	PRIMARY_GALLERY_STALE_TIME,
} from '#lib/websites/primary/constants/gallery.js';
import type { GetPrimaryGalleryArtworksQueryVariables } from '#lib/websites/primary/queries/__generated__/getPrimaryGalleryArtworks.generated.js';
import { GetPrimaryGalleryArtworksDocument } from '#lib/websites/primary/queries/__generated__/getPrimaryGalleryArtworks.generated.js';
import { getAppliedFilters } from '$global/features/filter-drawer/utils/getAppliedFilters/getAppliedFilters';
import { getAppliedSort } from '$global/features/filter-drawer/utils/getAppliedSort/getAppliedSort';
import { getQueryKeys } from '$global/query-utils/getQueryKeys';
import type { PrimaryGalleryPageLoadEvent } from '$routes/primary/collection/[collectionSlug]/types';

// Re-exported from their new home so existing importers keep working.
export { PRIMARY_GALLERY_LIMIT, PRIMARY_GALLERY_STALE_TIME };

export const primaryGalleryPageLoadGql = async ({
	parent,
	data,
	url,
}: PrimaryGalleryPageLoadEvent) => {
	const parentData = await parent();
	const queryClient: QueryClient = parentData.queryClient;
	const artworks = data.artworks;
	const filterSections = data.filterSections;
	const defaultSort = data.defaultSort;
	const searchParams = url.searchParams;

	queryClient.setQueryData(
		getQueryKeys(GetPrimaryGalleryArtworksDocument, data.variables),
		{
			pageParams: [0],
			pages: [artworks],
		}
	);

	const filter = getAppliedFilters({
		searchParams,
		filterSections,
	});

	const sort = getAppliedSort({
		searchParams,
		defaultSort,
	});

	const variables: GetPrimaryGalleryArtworksQueryVariables = {
		...data.variables,
		sort: [...sort],
		filter: {
			_and: [...(data.variables.filter?._and || []), ...filter],
		},
	};

	queryClient.prefetchInfiniteQuery({
		...getQuery(GetPrimaryGalleryArtworksDocument, variables),
		initialPageParam: 0,
		staleTime: PRIMARY_GALLERY_STALE_TIME,
	});

	return {
		...data,
		variables,
	};
};
